/**
 * Schema Visualization Component
 * 
 * Renders the database schema diagram showing entity relationships,
 * primary/foreign keys, and data architecture.
 */

import React, { useEffect, useRef } from 'react';
import * as d3 from 'd3';
import { schemaEntities, schemaColors } from './constants';

interface SchemaVisualizationProps {
  isVisible: boolean;
}

export default function SchemaVisualization({ isVisible }: SchemaVisualizationProps) {
  const schemaSvgRef = useRef<SVGSVGElement>(null);
  const schemaContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!schemaSvgRef.current || !schemaContainerRef.current || !isVisible) return;

    const svg = d3.select(schemaSvgRef.current);
    const container = schemaContainerRef.current;
    const rect = container.getBoundingClientRect();
    const width = Math.max(1400, rect.width - 32);
    const height = 800;

    svg.selectAll("*").remove();
    svg.attr("width", width).attr("height", height);

    // Create main group for zoom/pan
    const g = svg.append("g").attr("class", "schema-group");

    // Calculate layout positions for entities (updated to include junction tables)
    const entityWidth = 280;
    const baseEntityHeight = 60; // Header height
    const fieldHeight = 20;
    const spacingX = 380;
    const spacingY = 300;
    const minMargin = 30;
    const startX = 50;
    const startY = 50;

    // Calculate dynamic heights for each entity
    const getEntityHeight = (entity: any) => baseEntityHeight + (entity.fields.length * fieldHeight) + 20; // +20 for padding

    // Define positions for a more organized layout
    const companiesEntity = schemaEntities.find(e => e.name === 'companies')!;
    const activitiesEntity = schemaEntities.find(e => e.name === 'activities')!;
    const projectsEntity = schemaEntities.find(e => e.name === 'projects')!;
    const certificatesEntity = schemaEntities.find(e => e.name === 'certificates')!;
    const skillsEntity = schemaEntities.find(e => e.name === 'skills')!;
    const projectActivitiesEntity = schemaEntities.find(e => e.name === 'project_activities')!;
    const projectSkillsEntity = schemaEntities.find(e => e.name === 'project_skills')!;
    const certificateActivitiesEntity = schemaEntities.find(e => e.name === 'certificate_activities')!;

    // Layout: companies -> activities -> junction tables -> main entities -> skills
    const positions = [
      { x: startX, y: startY, entity: companiesEntity },
      { x: startX + spacingX, y: startY, entity: activitiesEntity },
      { x: startX + spacingX * 2, y: startY - 150, entity: projectActivitiesEntity },
      { x: startX + spacingX * 2, y: startY + 250, entity: certificateActivitiesEntity },
      { x: startX + spacingX * 3, y: startY - 150, entity: projectsEntity },
      { x: startX + spacingX * 3, y: startY + 250, entity: certificatesEntity },
      { x: startX + spacingX * 4, y: startY + 50, entity: projectSkillsEntity },
      { x: startX + spacingX * 5, y: startY + 50, entity: skillsEntity }
    ];

    // Draw relationships with proper PK-FK connections
    const drawConnection = (fromPos: any, toPos: any, fromField: string, toField: string, relType: string) => {
      // Calculate field positions
      const fromFieldIndex = fromPos.entity.fields.findIndex((f: any) => f.name === fromField);
      const toFieldIndex = toPos.entity.fields.findIndex((f: any) => f.name === toField);
      
      if (fromFieldIndex === -1 || toFieldIndex === -1) {
        console.warn(`Field not found: ${fromField} in ${fromPos.entity.name} or ${toField} in ${toPos.entity.name}`);
        return;
      }
      
      const fieldHeight = 20;
      const headerHeight = 55;
      const fieldPadding = 10;
      
      // Center the connection point on the field (both horizontally and vertically)
      // Match the exact field positioning used in entity drawing: fieldY = 55 + i * fieldHeight
      // Field background spans from (fieldY - 9) to (fieldY + 9), so center is at fieldY
      const fromY = fromPos.y + headerHeight + fromFieldIndex * fieldHeight;
      const toY = toPos.y + headerHeight + toFieldIndex * fieldHeight;
      
      // Determine direction and place connection points on appropriate sides
      const isGoingRight = toPos.x > fromPos.x;
      const isGoingLeft = toPos.x < fromPos.x;
      
      // For source: connect from the appropriate side based on direction
      // Place connection point at the middle of the field's side border
      const fromX = isGoingRight 
        ? fromPos.x + entityWidth                  // Right edge for rightward connections
        : fromPos.x;                               // Left edge for leftward connections
      
      // For target: connect to the appropriate side based on direction  
      const toX = isGoingRight
        ? toPos.x                                  // Left edge when coming from left
        : toPos.x + entityWidth;                   // Right edge when coming from right
      
      // Add connection point indicators on the fields
      const sourceIndicator = g.append("circle")
        .attr("cx", fromX)
        .attr("cy", fromY)
        .attr("r", 3)
        .attr("fill", "#64748B")
        .attr("stroke", "white")
        .attr("stroke-width", 1);
        
      const targetIndicator = g.append("circle")
        .attr("cx", toX)
        .attr("cy", toY)
        .attr("r", 3)
        .attr("fill", "#64748B")
        .attr("stroke", "white")
        .attr("stroke-width", 1);
      
      // Create path with proper routing
      // Adjust connection points for clean direct links from edge to edge
      const circleRadius = 3;
      const adjustedFromX = isGoingRight 
        ? fromX - circleRadius - 2  // End line before source circle (going right)
        : fromX + circleRadius + 2; // End line after source circle (going left)
      const adjustedToX = isGoingRight
        ? toX + circleRadius + 2    // End line after target circle (coming from left)
        : toX - circleRadius - 2;   // End line before target circle (coming from right)
      
      // Special routing for certificates-skills connection to avoid project_skills entity
      const isCertificatesSkillsConnection = fromPos.entity.name === 'certificates' && toPos.entity.name === 'skills';
      
      if (isCertificatesSkillsConnection) {
        // Route around project_skills entity by going below it
        // Need to account for entity padding: getEntityHeight adds +20 for padding at bottom
        const projectSkillsPos = positions.find(p => p.entity.name === 'project_skills');
        if (projectSkillsPos) {
          const projectSkillsEntityHeight = getEntityHeight(projectSkillsPos.entity);
          const avoidanceY = projectSkillsPos.y + projectSkillsEntityHeight + 30; // Go below the full entity + extra margin
          const midX = adjustedFromX + (adjustedToX - adjustedFromX) / 2;
          
          const path = g.append("path")
            .attr("d", `M ${adjustedFromX} ${fromY} L ${adjustedFromX + 50} ${fromY} L ${adjustedFromX + 50} ${avoidanceY} L ${adjustedToX - 50} ${avoidanceY} L ${adjustedToX - 50} ${toY} L ${adjustedToX} ${toY}`)
            .attr("stroke", "#64748B")
            .attr("stroke-width", 2)
            .attr("fill", "none")
            .attr("stroke-dasharray", relType === 'many-to-many' ? "5,5" : "none");
            
          // Add relationship label at the bottom horizontal segment
          g.append("rect")
            .attr("x", midX - 20)
            .attr("y", avoidanceY - 8)
            .attr("width", 40)
            .attr("height", 16)
            .attr("fill", "white")
            .attr("stroke", "#64748B")
            .attr("rx", 3);

          g.append("text")
            .attr("x", midX)
            .attr("y", avoidanceY + 3)
            .attr("text-anchor", "middle")
            .attr("font-size", "9px")
            .attr("fill", "#64748B")
            .text(relType === 'many-to-many' ? 'm:n' : '1:n');
        }
      } else {
        // Standard routing for other connections
        const midX = adjustedFromX + (adjustedToX - adjustedFromX) / 2;
        
        const path = g.append("path")
          .attr("d", `M ${adjustedFromX} ${fromY} L ${midX} ${fromY} L ${midX} ${toY} L ${adjustedToX} ${toY}`)
          .attr("stroke", "#64748B")
          .attr("stroke-width", 2)
          .attr("fill", "none")
          .attr("stroke-dasharray", relType === 'many-to-many' ? "5,5" : "none");

        // Add relationship label
        g.append("rect")
          .attr("x", midX - 20)
          .attr("y", (fromY + toY) / 2 - 8)
          .attr("width", 40)
          .attr("height", 16)
          .attr("fill", "white")
          .attr("stroke", "#64748B")
          .attr("rx", 3);

        g.append("text")
          .attr("x", midX)
          .attr("y", (fromY + toY) / 2 + 3)
          .attr("text-anchor", "middle")
          .attr("font-size", "9px")
          .attr("fill", "#64748B")
          .text(relType === 'many-to-many' ? 'm:n' : '1:n');
      }
    };

    // Add defs for masks
    const defs = svg.append("defs");

    // Draw entities FIRST (so they appear in background)
    positions.forEach(({ x, y, entity }) => {
      const currentEntityHeight = getEntityHeight(entity);
      
      const entityGroup = g.append("g")
        .attr("class", "entity")
        .attr("transform", `translate(${x}, ${y})`);

      // Entity container
      entityGroup.append("rect")
        .attr("width", entityWidth)
        .attr("height", currentEntityHeight)
        .attr("fill", "#475569")
        .attr("stroke", schemaColors[entity.type].border)
        .attr("stroke-width", 2)
        .attr("rx", 8)
        .style("filter", "drop-shadow(0px 4px 6px rgba(0, 0, 0, 0.1))");

      // Entity header
      entityGroup.append("rect")
        .attr("width", entityWidth)
        .attr("height", 40)
        .attr("fill", schemaColors[entity.type].bg)
        .attr("rx", 8)
        .attr("ry", 8)
        .style("mask", `url(#headerMask-${entity.name})`);

      // Add mask for rounded corners only on top
      const mask = defs.append("mask").attr("id", `headerMask-${entity.name}`);
      mask.append("rect").attr("width", entityWidth).attr("height", 40).attr("fill", "white").attr("rx", 8);
      mask.append("rect").attr("y", 20).attr("width", entityWidth).attr("height", 20).attr("fill", "white");

      // Entity name
      entityGroup.append("text")
        .attr("x", entityWidth / 2)
        .attr("y", 25)
        .attr("text-anchor", "middle")
        .attr("font-size", "14px")
        .attr("font-weight", "600")
        .attr("fill", schemaColors[entity.type].text)
        .text(entity.name);

      // Entity type indicator
      entityGroup.append("text")
        .attr("x", entityWidth - 10)
        .attr("y", 15)
        .attr("text-anchor", "end")
        .attr("font-size", "10px")
        .attr("fill", schemaColors[entity.type].text)
        .attr("opacity", 0.8)
        .text(entity.type.toUpperCase());

      // Fields
      entity.fields.forEach((field, i) => {
        const fieldY = 55 + i * fieldHeight;
        const fieldGroup = entityGroup.append("g");

        // Field background for special types
        if (field.isPrimaryKey || field.isForeignKey) {
          fieldGroup.append("rect")
            .attr("x", 5)
            .attr("y", fieldY - 9)
            .attr("width", entityWidth - 10)
            .attr("height", 18)
            .attr("fill", field.isPrimaryKey ? schemaColors.pk.bg : schemaColors.fk.bg)
            .attr("rx", 3);
        }

        // Field name
        fieldGroup.append("text")
          .attr("x", 10)
          .attr("y", fieldY + 2)
          .attr("font-size", "11px")
          .attr("font-weight", field.isPrimaryKey ? "600" : "normal")
          .attr("fill", field.isPrimaryKey ? schemaColors.pk.text : 
                       field.isForeignKey ? schemaColors.fk.text : schemaColors.field.text)
          .text(field.name + (field.isPrimaryKey ? " (PK)" : field.isForeignKey ? " (FK)" : ""));

        // Field type
        fieldGroup.append("text")
          .attr("x", entityWidth - 10)
          .attr("y", fieldY + 2)
          .attr("text-anchor", "end")
          .attr("font-size", "10px")
          .attr("fill", "#94A3B8")
          .text(field.type + (field.isRequired ? "" : "?"));
      });
    });

    // Draw specific connections with correct PK-FK mappings AFTER entities (so they appear in foreground)
    const companiesPos = positions.find(p => p.entity.name === 'companies');
    const activitiesPos = positions.find(p => p.entity.name === 'activities');
    const projectsPos = positions.find(p => p.entity.name === 'projects');
    const certificatesPos = positions.find(p => p.entity.name === 'certificates');
    const skillsPos = positions.find(p => p.entity.name === 'skills');
    const projectActivitiesPos = positions.find(p => p.entity.name === 'project_activities');
    const projectSkillsPos = positions.find(p => p.entity.name === 'project_skills');
    const certificateActivitiesPos = positions.find(p => p.entity.name === 'certificate_activities');

    // Direct one-to-many relationships
    if (companiesPos && activitiesPos) {
      drawConnection(companiesPos, activitiesPos, 'id', 'companyId', 'one-to-many');
    }

    // Many-to-many relationships through junction tables
    if (activitiesPos && projectActivitiesPos) {
      drawConnection(activitiesPos, projectActivitiesPos, 'id', 'activityId', 'one-to-many');
    }
    if (projectsPos && projectActivitiesPos) {
      drawConnection(projectsPos, projectActivitiesPos, 'id', 'projectId', 'one-to-many');
    }
    if (activitiesPos && certificateActivitiesPos) {
      drawConnection(activitiesPos, certificateActivitiesPos, 'id', 'activityId', 'one-to-many');
    }
    if (certificatesPos && certificateActivitiesPos) {
      drawConnection(certificatesPos, certificateActivitiesPos, 'id', 'certificateId', 'one-to-many');
    }
    if (projectsPos && projectSkillsPos) {
      drawConnection(projectsPos, projectSkillsPos, 'id', 'projectId', 'one-to-many');
    }
    if (skillsPos && projectSkillsPos) {
      drawConnection(skillsPos, projectSkillsPos, 'name', 'skillName', 'one-to-many');
    }
    
    // Semantic connection: certificates.skills array field to skills table
    // This represents the array field relationship even though it's not a proper FK
    if (certificatesPos && skillsPos) {
      drawConnection(certificatesPos, skillsPos, 'skills', 'name', 'many-to-many');
    }

    // Add zoom behavior
    const zoom = d3.zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.3, 2])
      .on("zoom", (event) => {
        g.attr("transform", event.transform);
      });

    svg.call(zoom);

    // Set initial zoom to fit the whole diagram
    const bounds = g.node()?.getBBox();
    if (bounds) {
      const scale = Math.min(width / (bounds.width + 100), height / (bounds.height + 100), 0.8);
      const translateX = (width - bounds.width * scale) / 2 - bounds.x * scale;
      const translateY = (height - bounds.height * scale) / 2 - bounds.y * scale;
      
      svg.call(zoom.transform, d3.zoomIdentity.translate(translateX, translateY).scale(scale));
    }

  }, [isVisible]);

  return (
    <div ref={schemaContainerRef} className="relative w-full h-[800px] bg-background overflow-auto">
      <svg 
        ref={schemaSvgRef}
        className="w-full h-full"
      />
      
      {/* Schema Legend */}
      <div className="absolute top-4 right-4 bg-card/95 backdrop-blur rounded-lg p-3 text-xs border shadow-sm">
        <h4 className="font-medium mb-2">Schema Legend</h4>
        <div className="space-y-1">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-yellow-100 border border-yellow-400 rounded"></div>
            <span>Primary Key (PK)</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-blue-100 border border-blue-400 rounded"></div>
            <span>Foreign Key (FK)</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-6 h-0.5 border-t-2 border-gray-400"></div>
            <span>One-to-Many (1:n)</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-6 h-0.5 border-t-2 border-gray-400 border-dashed"></div>
            <span>Many-to-Many (m:n)</span>
          </div>
        </div>
      </div>
    </div>
  );
}
